import { Component, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { th } from 'date-fns/locale';
import { MaskPipe } from 'ngx-mask';
import Utils from 'src/app/helpers/utils';
import { ApiService } from 'src/app/services/api/api.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { ToastService } from 'src/app/services/toast/toast.service';
import {
  BreadcrumbService,
  BreadcrumbItem,
} from 'src/app/services/breadcrumb/breadcrumb.service';
import { DRAFTS_PAGE_URL, draftsTabUrl } from 'src/app/data/draft-tabs';

@Component({
  selector: 'app-dashboard-drafts-bonds',
  templateUrl: './dashboard-drafts-bonds.component.html',
  styleUrls: ['./dashboard-drafts-bonds.component.scss'],
})
export class DashboardDraftsBondsComponent implements OnInit {
  form = this.formBuilder.group({
    draft_document_name: [null, []],

    start_date: [
      null,
      [
        Utils.validateWhenNotEmpty(Utils.validateDateValid),
        Utils.validateWhenNotEmpty(Utils.validateMaxMinToday),
      ],
    ],
    end_date: [
      null,
      [
        Utils.validateWhenNotEmpty(Utils.validateDateValid),
        Utils.validateWhenNotEmpty(Utils.validateMaxMinToday),
      ],
    ],
    active: [null, []],

    roles: [[], []],
    agents: [[], []],
    coobligation: [null, []],
    risk: [null, []],

    content: [null, []],
  });

  applicableItems = [];

  coobligationItems = [
    { label: 'Sem coobrigação', value: false },
    { label: 'Com coobrigação', value: true },
  ];

  riskItems = [
    { label: 'Com risco', value: 'with_risk' },
    { label: 'Sem risco', value: 'no_risk' },
  ];

  agentItems = [];
  statusItems = [
    { label: 'Ativo', value: true },
    { label: 'Inativo', value: false },
    // { label: 'Rascunho', value: false },
    // { label: 'Erro', value: false },
  ];

  roles: Role[] = [];
  agents: Agent[] = [];

  drafts: any[] = [];
  loadingDrafts: boolean = true;

  showFilterModal = false;
  warningModal: boolean = false;
  /**
   * Padrão de Filtros:
   * - hasFilters só deve ser true APÓS aplicar os filtros (método submitForm())
   * - NÃO atualizar hasFilters no valueChanges do form
   * - hasFilters deve ser atualizado apenas quando:
   *   1. submitForm() executar com sucesso (após a requisição)
   *   2. resetForm() for chamado (define como false)
   *   3. toggleFilterModal() quando há filtros salvos no storage
   */
  hasFilters = false;
  appliedFilters: Array<{ label: string; value: string }> = [];

  permissionObj = {
    canAdd: true,
    canChange: true,
    canEdit: true,
    canFilter: true,
  };

  canViewVariables: boolean = false;
  errorVariables: InactiveVariable[] = [];
  loadingVariables: boolean = false;
  selectedDraft: string = '';

  count: number = 0;
  offset: number = 0;
  results_per_page = 0;

  modelsOptions: SelectItem[] = [];
  fundsOptions: SelectItem[] = [];

  // Propriedades para o warning modal
  showStatusModal = false;
  selectedBond: any = null;
  isActive: boolean = false;

  constructor(
    private router: Router,
    private apiService: ApiService,
    private toastService: ToastService,
    private formBuilder: FormBuilder,
    private toast: ToastService,
    private authService: AuthService,
    private maskPipe: MaskPipe,
    private breadcrumbService: BreadcrumbService
  ) {}

  ngOnInit(): void {
    this.authService.canViewPage(['de_can_view_bonds']);

    this.setPermissions();
    this.setBreadcrumb();

    this.fetchData();
  }

  async fetchData() {
    let requests = [this.getModels()];

    // Construir filtros a partir do formulário (se houver valores)
    const filters: any = {};
    const values = this.form.value;
    
    Object.entries(values).forEach(([key, value]: any) => {
      if (value !== null && value !== '' && value !== undefined) {
        if (Array.isArray(value) && value.length === 0) {
          return;
        }
        // Verificar se o campo tem erro de validação antes de incluir
        const control = this.form.get(key);
        if (control && control.valid) {
          let newValue = value;
          if (key === 'start_date' || key === 'end_date') {
            newValue = value.split('/').reverse().join('-');
          }
          filters[key] = newValue;
        }
      }
    });

    // Se houver filtros válidos, aplicar
    if (Object.keys(filters).length > 0) {
      this.hasFilters = true;
      this.generateFilterTags(this.form.value);
      requests.push(this.fetchDrafts(1, filters));
    } else {
      this.hasFilters = false;
      this.appliedFilters = [];
      requests.push(this.fetchDrafts());
    }

    await Promise.all(requests);
    this.loadingDrafts = false;
  }

  renderLabel(item: any) {
    return this.modelsOptions.find((option) => option.value === item)?.label;
  }

  renderFundLabel(item: any[]) {
    return item.map((fund) => fund.name).join(', ');
  }

  async fetchDrafts(page = 1, params = {}) {
    const newParams = params;

    Object.keys(newParams).forEach((key) => {
      if (typeof newParams[key] === 'boolean') {
        newParams[key] = String(newParams[key]);
      }
    });

    try {
      const { count, results, offset, results_per_page } =
        await this.apiService.get({
          route: 'api/fund_document/',
          token: true,
          params: { page, ...newParams },
        });

      this.drafts = results;
      this.count = count;
      this.offset = offset;
      this.results_per_page = results_per_page;
    } catch (error) {
      this.toastService.show('error', 'Erro', 'Falha ao carregar as minutas');
    }
  }

  redirectToForm(id?: number) {
    this.router.navigateByUrl(`/app/drafts/bonds/${id ?? 'new'}`);
  }

  generateFilterTags(formValues: any) {
    this.appliedFilters = [];

    const fieldLabels: { [key: string]: string } = {
      draft_document_name: 'Template utilizado',
      active: 'Status',
      start_date: 'Início da vigência',
      end_date: 'Fim da vigência',
      risk: 'Risco',
    };

    Object.entries(formValues).forEach(([key, value]: any) => {
      if (value !== null && value !== '' && value !== undefined) {
        if (Array.isArray(value) && value.length === 0) {
          return;
        }
        let displayValue = value;

        // Tratamento especial para status
        if (key === 'active') {
          const statusOption = this.statusItems.find((s) => s.value === value);
          displayValue = statusOption ? statusOption.label : value;
        }

        if (key === 'risk') {
          const riskOption = this.riskItems.find((item) => item.value === value);
          displayValue = riskOption ? riskOption.label : value;
        }

        // Tratamento especial para datas
        if (key === 'start_date' || key === 'end_date') {
          displayValue = value;
        }

        if (fieldLabels[key]) {
          // Combinar label e valor no formato "label: valor"
          const combinedLabel = `${fieldLabels[key]}: ${String(displayValue)}`;
          this.appliedFilters.push({
            label: combinedLabel,
            value: String(displayValue),
          });
        }
      }
    });
  }

  toggleFilterModal() {
    this.showFilterModal = !this.showFilterModal;

    if (this.showFilterModal) {
      // Se houver filtros aplicados, restaurar no formulário
      if (this.hasFilters && this.appliedFilters.length > 0) {
        // Os valores já estão no formulário, apenas garantir que as tags estão atualizadas
        this.generateFilterTags(this.form.value);
      } else {
        this.hasFilters = false;
        this.appliedFilters = [];
      }
    }
  }

  toggleModal(show: boolean) {
    if (show) {
      this.toggleFilterModal();
    } else {
      this.showFilterModal = false;
    }
  }

  async resetForm() {
    this.loadingDrafts = true;
    this.hasFilters = false;
    this.appliedFilters = [];
    this.form.reset();
    await this.fetchDrafts();
    this.loadingDrafts = false;
  }

  renderDate(date: string) {
    return date.split('-').reverse().join('/');
  }

  async submitForm() {
    // Verificar se o formulário é válido antes de processar
    if (this.form.invalid) {
      // Marcar todos os campos como touched para mostrar erros
      Object.keys(this.form.controls).forEach((key) => {
        this.form.get(key)?.markAsTouched();
      });
      return;
    }

    try {
      this.loadingDrafts = true;
      
      const payload: any = {};

      // Construir payload apenas com campos válidos e preenchidos
      Object.entries(this.form.value).forEach(([key, value]: any) => {
        if (value !== null && value !== '' && value !== undefined) {
          // Verificar se o campo tem erro de validação antes de incluir
          const control = this.form.get(key);
          if (control && control.valid) {
            let newValue = value;
            if (key === 'start_date' || key === 'end_date') {
              newValue = value.split('/').reverse().join('-');
            }
            // Arrays vazios não devem ser incluídos
            if (Array.isArray(value) && value.length === 0) {
              return;
            }
            payload[key] = newValue;
          }
        }
      });

      await this.fetchDrafts(1, payload);

      // ✅ IMPORTANTE: hasFilters só é atualizado após aplicar os filtros com sucesso
      this.hasFilters = Object.keys(payload).length > 0;

      // ✅ Gerar as tags dos filtros aplicados
      this.generateFilterTags(this.form.value);

      this.showFilterModal = false;
    } catch (error) {
      this.toast.show('error', 'Erro', 'Ocorreu um erro ao fazer a filtragem.');
    }
    this.loadingDrafts = false;
  }

  setPermissions() {
    const permissions = Object.keys(this.permissionObj).map(
      (key) => `de_can_${key.split('can').join('').toLowerCase()}_bonds`
    );

    permissions.forEach((permission, idx) => {
      this.permissionObj[Object.keys(this.permissionObj)[idx]] =
        this.authService.verifyPermission(permission);
    });
  }

  setBreadcrumb(): void {
    const canViewDrafts =
      this.authService.verifyPermission('de_can_view_drafts');
    const breadcrumbs: BreadcrumbItem[] = [
      {
        label: 'Minutas',
        url: DRAFTS_PAGE_URL,
        clickable: canViewDrafts,
      },
      {
        label: 'Vínculos',
        url: draftsTabUrl('bonds'),
        clickable: false,
      },
    ];
    this.breadcrumbService.setBreadcrumbs(breadcrumbs);
  }

  openWarningModal(id: number) {
    this.loadingVariables = true;
    this.selectedDraft =
      this.drafts.find((draft) => draft.id === id)?.name || '';
    this.warningModal = true;
    this.checkDraftError(id);
  }

  closeWarningModal() {
    this.warningModal = false;
    this.errorVariables = [];
    this.selectedDraft = '';
    this.loadingVariables = false;
  }

  async checkDraftError(id: number) {
    try {
      const res = await this.apiService.get<VariableResponseValues>({
        route: `api/checking_minutes/?document_id=9`,
      });

      this.errorVariables = res.inactive_variables;
    } catch (error) {}

    this.loadingVariables = false;
  }

  async refreshData() {
    await this.changePageData(1);
  }

  async changePageData(page: number) {
    this.loadingDrafts = true;

    const filters: any = {};
    const values = this.form.value;

    Object.entries(values).forEach(([key, value]: any) => {
      if (value !== null && value !== '' && value !== undefined) {
        if (Array.isArray(value) && value.length === 0) {
          return;
        }
        const control = this.form.get(key);
        if (control && control.valid) {
          let newValue = value;
          if (key === 'start_date' || key === 'end_date') {
            newValue = value.split('/').reverse().join('-');
          }
          filters[key] = newValue;
        }
      }
    });

    await this.fetchDrafts(page, filters);
    this.loadingDrafts = false;
  }

  toggleDraftStatus(id: number) {
    const draft = this.drafts.find((draft) => draft.id === id);
    this.selectedBond = draft;
    this.isActive = draft.active;
    this.showStatusModal = true;
  }

  async confirmStatusChange() {
    try {
      this.loadingDrafts = true;

      const payload = {
        active: !this.isActive,
      };

      await this.apiService.patch({
        route: `api/fund_document/${this.selectedBond.id}/`,
        body: payload,
        token: true,
      });

      this.showStatusModal = false;
      this.fetchData();
      this.toast.show(
        'info',
        'Sucesso',
        `Modelo de minuta ${
          this.isActive ? 'inativado' : 'ativado'
        } com sucesso!`
      );
    } catch (error) {
      console.log(error);
      this.toast.show(
        'error',
        'Erro',
        'Ocorreu um erro ao tentar alterar o status da minuta.'
      );
    }
    this.loadingDrafts = false;
  }

  closeStatusModal() {
    this.showStatusModal = false;
    this.selectedBond = null;
    this.isActive = false;
  }

  getModalTitle(): string {
    return this.isActive ? 'Inativar minuta' : 'Ativar minuta';
  }

  getModalMessage(): string {
    return `Deseja ${
      this.isActive ? 'inativar' : 'ativar'
    } essa minuta? Essa ação irá ${
      this.isActive ? 'inativar' : 'ativar'
    } o modelo de minuta.`;
  }

  getConfirmText(): string {
    return `Sim, ${this.isActive ? 'inativar' : 'ativar'} minuta`;
  }

  async getModels() {
    try {
      const res = await this.apiService.get({
        route: 'api/draft/',
        token: true,
        params: {
          no_pagination: true,
        },
      });

      const models = res.map((item: any) => ({
        label: item.name,
        value: item.id,
      }));

      this.modelsOptions = models;
    } catch (error) {
      console.error(error);
      this.toast.show('error', 'Erro', 'Erro ao buscar modelos');
    }
  }

  renderRisk(risk?: string | null) {
    if (risk === 'with_risk') return 'Com risco';
    if (risk === 'no_risk') return 'Sem risco';
    return '-';
  }

  renderDisabledStatus(draft: DraftDocument) {
    if (draft.error) {
      return 'Erro';
    } else {
      return 'Desativado';
    }
  }

  redirectToVariables() {
    this.router.navigateByUrl('/app/drafts/variables');
    this.warningModal = false;
  }

  /**
   * Verifica se os filtros podem ser aplicados
   * Retorna false se houver campos inválidos ou se não houver nenhum filtro preenchido
   */
  get canApplyFilters(): boolean {
    // Verifica se o formulário é válido
    if (this.form.invalid) {
      return false;
    }

    // Verifica se há pelo menos um filtro preenchido
    const values = this.form.value;
    const hasAnyFilter = Object.entries(values).some(([key, value]: any) => {
      // Arrays vazios não contam como filtro
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return value !== null && value !== '' && value !== undefined;
    });

    return hasAnyFilter;
  }
}
